import { useEffect, useState } from "react";
import axios from "axios";

/** One day of real (human, bot-filtered) browser traffic for a public site. */
export type TrafficPoint = { date: string; pageViews: number; visits: number };

/** Real browser traffic from one country over the window (alpha-2 code → page views). Feeds the map. */
export type CountryTraffic = { code: string; count: number };

/** One slice of a categorical breakdown — browser, OS or device (mirrors the .NET AnalyticsBreakdownItem). */
export type AnalyticsBreakdownItem = { label: string; pageViews: number; visits: number };

/** Cloudflare Web Analytics (RUM) summary for ONE public instance (mirrors the .NET TrafficSummary). */
export type InstanceSummary = {
	configured: boolean;
	target: string; // "production" | "preview"
	host: string;
	days: number;
	pageViews: number;
	visits: number;
	series: TrafficPoint[];
	countries: CountryTraffic[];
	browsers: AnalyticsBreakdownItem[];
	operatingSystems: AnalyticsBreakdownItem[];
	devices: AnalyticsBreakdownItem[];
	reason?: string | null;
	generatedAt?: string;
};

/** Real audience for every public instance over the same window (mirrors the .NET AnalyticsOverview). */
export type AnalyticsOverview = {
	days: number;
	configured: boolean;
	reason?: string | null;
	instances: InstanceSummary[];
	generatedAt?: string;
};

/**
 * Cloudflare Web Analytics (RUM) audience for ALL public instances (production + preview) over the
 * last N days — the data behind the dashboard's per-instance cards and the visitor map. This is REAL,
 * bot-filtered browser traffic, filtered to the APA hostnames (no zone pollution). Read-only and
 * degrades gracefully: a missing token/account yields an unconfigured overview rather than an error.
 * One request for the whole section; the server fans out to both hosts and caches for 5 minutes.
 */
export const useAnalyticsOverview = (days = 7) => {
	const [data, setData] = useState<AnalyticsOverview | null>(null);
	const [loading, setLoading] = useState(true);
	const [nonce, setNonce] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		axios
			.get<AnalyticsOverview>("/api/analytics/overview", { params: { days } })
			.then((r) => {
				if (!cancelled) setData(r.data);
			})
			.catch(() => {
				if (!cancelled) setData(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [days, nonce]);

	return { data, loading, reload: () => setNonce((n) => n + 1) };
};
